import { yupResolver } from '@hookform/resolvers/yup';
import copy from 'copy-to-clipboard';
import React, { useEffect, useState } from 'react';
import { Form, InputGroup, Modal } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiCoin, BiLogoWhatsapp, BiTrash } from 'react-icons/bi';
import { CiUnlock } from 'react-icons/ci';
import { HiDocumentDuplicate, HiKey, HiUser } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useGenerateNewUserAccessTokenMutation, useGetPackageQuery, useUpdateWidgetSettingMutation } from '@/app/services/chatbot-rtk.services';
import { BannerHelper } from '@/components/common/BannerToast';
import { PSButton } from '@/components/ui/button';
import Utilities from '@/utils';

import PopoverWithAudio, { IPopoverWithAudioType } from '../../components/common/PopoverWithAudio';
import ProBadge from '../../components/ui/ProBadge';
import usePermission from '../../hooks/usePermission';
import { WrappedErrorBoundary } from '../WrappedErrorBoundary';
import ScenarioChatbotChart from './home/ScenarioChatbotChart';

type SetDomainType = {
  autoInstall: boolean;
  domains: { url: string }[];
};

const SettingTempPage = () => {
  const { t } = useTranslation();
  const permissions = usePermission();
  const navigate = useNavigate();

  const getClient = useGetPackageQuery();

  const [updateGeneralSettings, updateGeneralSettingsResults] = useUpdateWidgetSettingMutation();

  const [generate, { isLoading }] = useGenerateNewUserAccessTokenMutation();
  const [openModal, setOpenModal] = useState(false);

  const token = localStorage?.getItem(`LOUISEBOT_user_access_token`);

  const usedIA = getClient?.data.remainingSessions?.aiUsed || 0;
  const usedScenario = getClient?.data.remainingSessions?.scenarioUsed || 0;
  const usedTotal = usedIA + usedScenario;

  const sessionLimit = getClient?.data.remainingSessions?.sessionLimit || 0;

  const sumId = getClient?.data?.sum_identification;

  const isUnlimitedScenario = sessionLimit === -1;
  const plural = usedTotal > 1 ? 's' : '';

  const sessionText = isUnlimitedScenario
    ? t('Usage/UnlimitedSessionMessage')
    : t('Usage/SessionMessage', {
        usedTotal,
        sessionLimit,
        plural,
      });

  const remainedPurchasedSessions = getClient?.data.remainingSessions?.remainedPurchasedSessions || 0;

  //const remainedWhatsappSessions = getClient?.data.remainingSessions?.remainedWhatsappSessions || 0;
  const schema = Yup.object({
    autoInstall: Yup.boolean().required().label('Auto-install'),
    domains: Yup.array(
      Yup.object({
        url: Yup.string()
          /**
           * The maximum character limit for the last part of a domain (also known as the Top-Level Domain or TLD)  depends on the context:
           * Technically: Each label (including the TLD) in a domain name can have a maximum of 63 characters. This limit is defined by the underlying technical specifications of the Domain Name System (DNS).
           * Practically: Most domain registries enforce a stricter limit of 63 characters for the entire domain name, including all labels and the periods (dots) separating them. This ensures compatibility with various applications and software that might have shorter limits.
           * Therefore, it's best to keep the entire domain name, including the TLD, under 63 characters for guaranteed functionality.
           */
          .matches(/^(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,15}\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)/i, {
            message: t('DashboardSettingWidgetDefaultDomain/er-01'),
          })
          .required(),
      })
    )

      .required()
      .label('Domains'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
    getValues,
  } = useForm<SetDomainType>({
    defaultValues: {
      autoInstall: false,
      domains: [{ url: '' }],
    },
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'domains',
  });

  const onSubmit = (data: SetDomainType) => {
    updateGeneralSettings({
      ...data,
      domains: [...data.domains.map(domain => `https://` + domain.url)],
    })
      .unwrap()
      .then(res => {
        toast.success('Successfully updated');
      })
      .catch(err => {
        toast.error(err.data.message || err.message);
      });
  };

  const AutoInstallPopData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingWidgetDefaultDomain/pop-01'),
  };

  useEffect(() => {
    if (getClient.isSuccess)
      reset({
        autoInstall: getClient.data?.config.widget_defaults.autoInstall ?? false,
        domains: (getClient.data?.config.widget_defaults.domains ?? []).map((domain: string) => ({
          url: domain.replace('https://', ''),
        })),
      });
  }, [getClient.isFetching, getClient.isSuccess]);

  const onGenerate = () => {
    generate({})
      .then(res => {
        setOpenModal(false);
        localStorage.setItem(`LOUISEBOT_user_access_token`, res?.data?.accessToken);
        toast.success(t('DashboardSettingWidgetDefaultDomain/m-01'));
      })
      .catch(err => {
        toast.error(err.data.message || err.message);
      });
  };

  return (
    <section id="SessionTracking" className="setting-page tw-p-5 tw-flex tw-flex-col tw-gap-4">
      <div className="tw-p-5 tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
        <div className="tw-w-full tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-4">
          <div className="tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
            <BannerHelper message={t('HelpBanner/Usage/hint')} />
            <div className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('Usage/Title')}</div>
            <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Usage/Desc2')}</div>
            <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Usage/Desc3')}</div>
            <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Usage/Desc4')}</div>
            <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Usage/Desc5')}</div>
            <div className="tw-w-full tw-mt-2">
              <div className="tw-mt-2 tw-shadow-md tw-p-2">
                <div className="tw-text-gray-700 tw-text-md tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Usage/DescSC1')}</div>
                <div className="tw-flex tw-justify-start md:tw-flex-row tw-items-center tw-gap-2 tw-my-2">
                  <ScenarioChatbotChart />
                </div>
                <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mt-2">{sessionText}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-p-5 tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
        <div className="tw-w-full tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-4">
          <div className="tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
            <BannerHelper message={t('HelpBanner/Usage/hintExtra')} />
            <div className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('Usage/TitleExtra')}</div>
            <div className=" tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Usage/SessionUsageOrder')}</div>
            <div className=" tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">{t('Usage/ExtraSessionPolicy')}</div>
            <div className="tw-w-full tw-mt-2">
              <div className="tw-text-gray-700 tw-text-md tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                <PSButton
                  id="btn-setting-create"
                  size="sm"
                  onClick={() => {
                    navigate('../billing-credit-create');
                  }}
                  className="d-flex gap-2 align-items-center"
                  icon={<BiCoin className="fs-5 fw-bold" size={20} />}
                >
                  <span className="fw-bold">{t('Usage/DescExtraBuy')}</span>
                </PSButton>
              </div>
              <div className="tw-mt-2 tw-shadow-md tw-p-2">
                <div className="tw-ml-2 tw-flex tw-justify-start md:tw-flex-row tw-items-center tw-gap-2 tw-my-2">
                  <BiCoin size={20} color="orange" />
                  {t('Usage/RemainingExtraSession', {
                    sessionLeft: remainedPurchasedSessions,
                  })}
                </div>
                {/* <div className="tw-ml-2 tw-flex tw-justify-start md:tw-flex-row tw-items-center tw-gap-2 tw-my-2">
                  <BiLogoWhatsapp size={20} color="green" />
                  {t('Usage/RemainingWhatsAppSession', {
                    sessionLeft: remainedWhatsappSessions,
                  })}
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-p-5 tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
        <div className="tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
          <BannerHelper message={t('HelpBanner/Domains/hint')} />
          <div className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('DashboardSettingWidgetDefaultDomain/title')}</div>
          <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('DashboardSettingWidgetDefaultDomain/des-01')}
          </div>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)} className="tw-w-full">
          {/* Auto install */}
          <Form.Group className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
            <Form.Check
              type="switch"
              id="autoInstall"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t('DashboardSettingWidgetDefaultDomain/l-01')}
              {...register('autoInstall')}
            />
            <PopoverWithAudio data={AutoInstallPopData} />
          </Form.Group>

          {/* Domains */}
          {fields.map((field, index) => (
            <div key={index} className="tw-mb-4">
              <div key={field.id} className="md:tw-w-[350px] tw-w-[85%] tw-flex tw-items-center tw-gap-2">
                <InputGroup className="tw-grow tw-shrink-0">
                  <InputGroup.Text>https://</InputGroup.Text>
                  <Form.Control type="text" isInvalid={!!errors.domains?.[index]?.url} {...register(`domains.${index}.url` as const)} />
                </InputGroup>

                <div>
                  <BiTrash
                    size={22}
                    onClick={() => {
                      remove(index);
                      handleSubmit(onSubmit)(); // Then submit the form
                    }}
                    className="tw-text-red-500 tw-cursor-pointer tw-hover:tw-text-red-700"
                  />
                </div>
              </div>
              <Form.Control.Feedback type="invalid" className={errors.domains?.[index]?.url ? `tw-inline-flex` : ''}>
                {errors?.domains?.[index]?.url?.message}
              </Form.Control.Feedback>
            </div>
          ))}

          {!!errors.domains && <div className="tw-text-red-600 tw-my-4">{errors.domains.message}</div>}

          <PSButton
            id="btn-setting-append"
            variant="secondary"
            type="button"
            onClick={() => append({ url: '' })}
            icon={<CiUnlock size={22} className="tw-text-gray-400 tw-mr-4" />}
            title={t('DashboardSettingWidgetDefaultDomain/add-domains')}
          />

          <hr className="tw-border-gray-400 tw-my-8" />

          <div className="tw-flex tw-justify-end">
            <PSButton
              id="btn-setting-submit"
              disabled={updateGeneralSettingsResults.isLoading || !isDirty}
              type="submit"
              title={t('DashboardSettingWidgetDefaultDomain/apply')}
            />
          </div>
        </Form>
      </div>

      {!permissions.settingsPage.widget.customization.poweredBy && (
        <div className="tw-p-5 tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
          <div className="tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
            <div className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('DashboardSettingWidgetDefaultDomain/key1')}</div>
          </div>
          <Form.Group className="tw-flex tw-items-center tw-gap-2">
            <Form.Check
              disabled={!permissions.settingsPage.widget.customization.poweredBy}
              defaultChecked={false}
              type="switch"
              id="poweredBy"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t('DashboardSettingCustomizeChat/removePoweredBy')}
            />
            <ProBadge className="tw-ms-2" />
          </Form.Group>
        </div>
      )}

      <div className="tw-p-5 tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
        <div className="tw-w-full tw-flex tw-flex-col tw-gap-4">
          <BannerHelper message={t('HelpBanner/Token/hint')} />
          <div className="tw-w-full tw-flex tw-flex-wrap tw-justify-between tw-items-start md:tw-items-center tw-gap-4 tw-flex-col md:tw-flex-row">
            <div className="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-semibold  tw-leading-tight">
              <HiKey size={20} />
              <span>{t('DashboardSettingWidgetDefaultDomain/accessToken')}</span>
            </div>
            <div className="tw-flex tw-flex-col-reverse tw-justify-start  md:tw-flex-row tw-items-center tw-gap-2">
              <PSButton
                id="btn-setting-request"
                variant="outline"
                onClick={() => setOpenModal(true)}
                title={t('DashboardSettingWidgetDefaultDomain/requestNew')}
              />
              <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
                <HiDocumentDuplicate
                  size={20}
                  className="tw-text-gray-400 tw-cursor-pointer"
                  onClick={() => {
                    copy(token as string);
                    toast.success(t('DashboardSettingWidgetDefaultDomain/t-01'));
                  }}
                />
                <span translate="no" className="tw-text-gray-700 tw-text-sm tw-font-bold  tw-leading-tight">
                  {Utilities.renderHiddenToken(token as string)}
                </span>
              </div>
            </div>
          </div>

          <div className="tw-w-full tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-4">
            <div className="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-semibold  tw-leading-tight">
              <HiUser size={20} />
              <span>{t('DashboardSettingWidgetDefaultDomain/clientId')}</span>
            </div>
            <div className="tw-flex tw-flex-col-reverse tw-justify-start  md:tw-flex-row tw-items-center tw-gap-2">
              <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
                <HiDocumentDuplicate
                  size={20}
                  className="tw-text-gray-400 tw-cursor-pointer"
                  onClick={() => {
                    copy(sumId as string);
                    toast.success(t('DashboardSettingWidgetDefaultDomain/t-01'));
                  }}
                />
                <span translate="no" className="tw-text-gray-700 tw-text-sm tw-font-bold  tw-leading-tight">
                  {Utilities.renderHiddenToken(sumId as string)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal centered show={openModal} onHide={() => setOpenModal(false)} dialogClassName="tw-max-w-[366px]">
        <Modal.Body className="tw-py-5 tw-px-6">
          <div className="tw-flex tw-flex-col tw-w-full tw-items-center tw-justify-center tw-gap-y-3">
            <div className="tw-self-stretch tw-flex-col tw-justify-center tw-items-center tw-gap-2 tw-flex tw-my-[10px]">
              <div className="tw-self-stretch tw-text-center tw-text-gray-700 tw-text-xl tw-font-semibold tw-font-['Lexend'] tw-leading-7">
                {t('DashboardSettingWidgetDefaultDomain/t-02')}
              </div>
              <div className="tw-self-stretch tw-text-center tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend']">
                {t('DashboardSettingWidgetDefaultDomain/l-02')}
              </div>
            </div>
            <div className="tw-flex tw-flex-col tw-w-full tw-gap-y-4 tw-my-4">
              <PSButton
                id="btn-setting-set-default"
                variant="warning"
                onClick={onGenerate}
                isLoading={isLoading}
                title={t('DashboardSettingWidgetDefaultDomain/btn-02')}
              />
              <PSButton
                id="btn-setting-delete"
                variant="ghost"
                onClick={() => setOpenModal(false)}
                className="tw-grow tw-shrink tw-basis-0 tw-h-[42px] tw-px-[17px] tw-py-[9px]  hover:tw-bg-white hover:tw-text-gray-800 tw-border tw-border-solid tw-border-gray-300 hover:tw-border-gray-300  tw-rounded-[99px] tw-justify-center tw-items-center tw-flex tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                title={t('DeletAccount/btn-01')}
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default WrappedErrorBoundary(SettingTempPage);
